import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Avatar,
  IconButton,
  Button,
  Stack,
  TextField,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  FormControl,
  OutlinedInput,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import apiService from "../../service/Api";
import { CalendarIcon, LocationIcon } from "../../components/icons";
import ButtonIcon from "../../components/icons/PenPaperIcon";
import { tokens } from "../../theme";

interface SponsorshipResponse {
  id: number;
  event_datetime: string;
  venue_name?: string | null;
  request_type: string;
  status: ActionType;
  event: {
    title: string;
    special_notes: string;
    notes: string;
    event_flyer_image: string;
  };
  contact: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  created_at: string;
}

type ActionType = "PENDING" | "ACCEPTED" | "DECLINED" | "MAYBE";

// These will be replaced by theme tokens inside the component

export default function RequestDetails() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const cardBg = colors.primary[900];
  const mutedCard = colors.primary[800];
  const textPrimary = colors.primary[700];
  const textSecondary = colors.primary[700];
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState<SponsorshipResponse>();
  const [status, setStatus] = useState<ActionType>("PENDING");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [editNotes, setEditNotes] = useState(false);
  const [editInternalNotes, setEditInternalNotes] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionType | null>(null);
  const [openImage, setOpenImage] = useState(false);

  const declineReasons = [
    {
      label: "Budget Limit",
      message:
        "Thank you for your request. Unfortunately we cannot support this event due to budget limitations.",
    },
    {
      label: "Not aligned",
      message:
        "Thank you for reaching out. Unfortunately this event does not align with our current sponsorship focus.",
    },
    {
      label: "Already booked",
      message:
        "Thank you for your request. Unfortunately our sponsorship slots are fully booked.",
    },
  ];

  const [reason, setReason] = useState(declineReasons[0].label);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const response = await apiService.get<SponsorshipResponse>(
          `/api/bookings/quote-request/${id}/`,
        );

        const data = response.data;
        setRequest(data);
        setStatus(data.status || "PENDING");
        setNotes(data.event?.notes || "");
        setInternalNotes(data.event?.special_notes || "");
      } catch (err) {
        console.error("Failed to fetch request", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [id]);

  const formatImageUrl = (url?: string) => {
    if (!url) return "";
    return url.replace(
      "http://localhost:8000",
      "https://j8s6hw8d-8000.uks1.devtunnels.ms",
    );
  };

  // const sendWhatsapp = (phone: string, msg: string) => {
  //   const encoded = encodeURIComponent(msg);
  //   const url = `https://wa.me/${phone}?text=${encoded}`;
  //   window.open(url, "_blank");
  // };

  // const updateStatus = async (newStatus: ActionType) => {
  //   await apiService.patch(`/api/bookings/quote-request/${id}/`, {
  //     status: newStatus.toUpperCase(),
  //   });
  //   setStatus(newStatus);
  // };

  // const handleConfirm = async () => {
  //   if (!selectedAction) return;

  //   await updateStatus(selectedAction);

  //   if (selectedAction !== "ACCEPTED") {
  //     sendWhatsapp(request?.contact?.phone || "", message);
  //   }

  //   setOpenModal(false);
  // };

  const normalizePhoneForWhatsapp = (phone?: string) => {
    if (!phone) return "";

    // remove spaces, dashes, brackets
    let cleaned = phone.replace(/[^\d+]/g, "");

    // convert leading + to international format for wa.me
    cleaned = cleaned.replace(/^\+/, "");

    // handle local Zimbabwe numbers like 0771234567 -> 263771234567
    if (cleaned.startsWith("0")) {
      cleaned = `263${cleaned.slice(1)}`;
    }

    return cleaned;
  };

  const openWhatsapp = (phone: string, msg: string) => {
    const normalizedPhone = normalizePhoneForWhatsapp(phone);
    if (!normalizedPhone) return;

    const encoded = encodeURIComponent(msg || "");
    const url = `https://wa.me/${normalizedPhone}?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const updateStatus = async (newStatus: ActionType) => {
    await apiService.patch(`/api/bookings/quote-request/${id}/`, {
      status: newStatus,
    });

    setStatus(newStatus);
  };

  const handleConfirm = async () => {
    if (!selectedAction || !request?.contact?.phone) return;

    try {
      setSaving(true);

      await updateStatus(selectedAction);

      const whatsappMessage =
        message?.trim() ||
        (selectedAction === "ACCEPTED"
          ? "Hello, your sponsorship request has been approved."
          : selectedAction === "DECLINED"
            ? "Hello, unfortunately your sponsorship request was not approved."
            : "Hello, could you please share a few more details about the event?");

      openWhatsapp(request.contact.phone, whatsappMessage);
      setOpenModal(false);
    } catch (error) {
      console.error("Failed to confirm action", error);
    } finally {
      setSaving(false);
    }
  };

  // const handleStatusChange = (value: ActionType) => {
  //   if (value === "PENDING") return;
  //   setSelectedAction(value);

  //   if (value === "DECLINED") {
  //     setMessage(declineReasons[0].message);
  //     setReason(declineReasons[0].label);
  //   } else if (value === "MAYBE") {
  //     setMessage(
  //       "Hello, could you please share a few more details about the event?",
  //     );
  //   } else if (value === "ACCEPTED") {
  //     setMessage("");
  //   }

  //   setOpenModal(true);
  // };

  const handleStatusChange = (value: ActionType) => {
    if (value === "PENDING") return;

    setSelectedAction(value);

    if (value === "DECLINED") {
      setReason(declineReasons[0].label);
      setMessage(declineReasons[0].message);
    } else if (value === "MAYBE") {
      setMessage(
        "Hello, could you please share a few more details about the event?",
      );
    } else if (value === "ACCEPTED") {
      setMessage(
        "Hello, your sponsorship request has been approved. We look forward to supporting your event.",
      );
    }

    setOpenModal(true);
  };
  const updateNotes = async (type: "notes" | "internal") => {
    try {
      setSaving(true);

      const payload: {
        event: {
          notes?: string;
          special_notes?: string;
        };
      } = { event: {} };

      if (type === "notes") payload.event.notes = notes;
      if (type === "internal") payload.event.special_notes = internalNotes;

      await apiService.patch(`/api/bookings/quote-request/${id}/`, payload);
    } catch (error) {
      console.error("Failed to update notes", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const url = formatImageUrl(
        request?.event?.event_flyer_image.replace("http", "https"),
      );
      if (!url) return;

      const response = await fetch(url, {
        credentials: "include",
      });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = url.split("/").pop() || "event_flyer";
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const modalContent: Record<ActionType, { title: string; subtitle: string }> =
    {
      DECLINED: {
        title: "Decline Sponsorship Request",
        subtitle: "Select a reason template for declining this request.",
      },
      MAYBE: {
        title: "Request Additional Information",
        subtitle: "Send a WhatsApp message to request more event details.",
      },
      ACCEPTED: {
        title: "Approve Sponsorship Request",
        subtitle: "Confirm acceptance of this sponsorship request.",
      },
      PENDING: {
        title: "",
        subtitle: "",
      },
    };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: colors.primary[800],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: textSecondary }}>
          Loading request...
        </Typography>
      </Box>
    );
  }

  const fullName =
    `${request?.contact?.first_name || ""} ${request?.contact?.last_name || ""}`.trim();
  const initials =
    `${request?.contact?.first_name?.[0] || ""}${request?.contact?.last_name?.[0] || ""}`.trim();

  return (
    <Box
      sx={{
        minHeight: "100vh",
      }}
    >
      {/* TOP BAR */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            minWidth: "auto",
            px: 2,
            py: 1.1,
            borderRadius: "999px",
            bgcolor: "#F7F8FA",
            color: "#5E6B7A",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            border: "1px solid #EEF1F4",
            "&:hover": {
              bgcolor: "#F1F4F7",
              boxShadow: "none",
            },
          }}
        >
          BACK
        </Button>

        <FormControl size="small">
          <Select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value as ActionType)}
            input={<OutlinedInput notched={false} />}
            IconComponent={KeyboardArrowDownRoundedIcon}
            sx={{
              minWidth: 180,
              height: 44,
              borderRadius: "999px",
              bgcolor: "#F7F8FA",
              color: "#5E6B7A",
              fontWeight: 500,
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#EEF1F4",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E4E8EE",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D7DEE7",
              },
            }}
          >
            <MenuItem value="PENDING">Pending Decision</MenuItem>
            <MenuItem value="ACCEPTED">Accepted</MenuItem>
            <MenuItem value="DECLINED">Declined</MenuItem>
            <MenuItem value="MAYBE">Maybe</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* MAIN LAYOUT */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "420px 1fr" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* LEFT PANEL */}
        <Card
          elevation={0}
          sx={{
            bgcolor: cardBg,
            borderRadius: "18px",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* PROFILE HEADER */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Avatar
                sx={{
                  width: 54,
                  height: 54,
                  bgcolor: "#0F4D92",
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {initials || "TD"}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: textPrimary,
                    lineHeight: 1.2,
                  }}
                >
                  {fullName || "Tawanda Dube"}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.4,
                    fontSize: 14,
                    color: "#2F3B4A",
                    lineHeight: 1.2,
                  }}
                >
                  Host/Organizer
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <IconButton
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "#FFF1F2",
                    color: "#EA5B71",
                    border: "1px solid #F6D9DE",
                    "&:hover": { bgcolor: "#FFE8EB" },
                  }}
                >
                  <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                <IconButton
                  size="small"
                  component="a"
                  href={`https://wa.me/${request?.contact?.phone}`}
                  target="_blank"
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "#EEFCEF",
                    color: "#35B66B",
                    border: "1px solid #D8F0DD",
                    "&:hover": { bgcolor: "#E6F9E9" },
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 18 }} />
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() =>
                    navigator.clipboard.writeText(request?.contact?.phone || "")
                  }
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "#F6F7F9",
                    color: "#677281",
                    border: "1px solid #E8ECF1",
                    "&:hover": { bgcolor: "#EFF2F5" },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Box>

            {/* NOTE BLOCK */}
            {[
              {
                title: "Internal Notes",
                value: internalNotes,
                setValue: setInternalNotes,
                editing: editInternalNotes,
                setEditing: setEditInternalNotes,
                saveType: "internal" as const,
              },
              {
                title: "Notes",
                value: notes,
                setValue: setNotes,
                editing: editNotes,
                setEditing: setEditNotes,
                saveType: "notes" as const,
              },
            ].map((item) => (
              <Box
                key={item.title}
                sx={{
                  bgcolor: mutedCard,
                  borderRadius: "14px",
                  p: 1.75,
                  mb: 2,
                  border: "1px solid #EDF1F5",
                }}
              >
                <Box
                  sx={{
                    mb: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#505C6B",
                    }}
                  >
                    {item.title}
                  </Typography>

                  <IconButton
                    disabled={saving}
                    size="small"
                    onClick={async () => {
                      if (item.editing) {
                        await updateNotes(item.saveType);
                      }
                      item.setEditing(!item.editing);
                    }}
                    sx={{
                      width: 28,
                      height: 28,
                      color: "#9A5B37",
                    }}
                  >
                    {item.editing ? (
                      <CheckIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <ButtonIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  value={item.value}
                  onChange={(e) => item.setValue(e.target.value)}
                  disabled={!item.editing}
                  variant="outlined"
                  placeholder={`Add ${item.title.toLowerCase()}`}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      p: 0,
                      bgcolor: "transparent",
                      borderRadius: 2,
                      alignItems: "flex-start",
                      "& fieldset": {
                        borderWidth: item.editing ? 1 : 0,
                        borderColor: "#D6DEE8",
                      },
                    },
                    "& textarea": {
                      padding: item.editing ? "10px 12px" : "0px",
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: "#5E6B7A",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Card>

        {/* RIGHT PANEL */}
        <Card
          elevation={0}
          sx={{
            bgcolor: cardBg,
            borderRadius: "18px",
            overflow: "hidden",
            height: "100%",
          }}
        >
          <Box
            sx={{
              p: 2,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "290px 1fr" },
              gap: 3,
              alignItems: "start",
            }}
          >
            {/* POSTER */}
            <Box>
              {request?.event?.event_flyer_image ? (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 275,
                    borderRadius: "16px",
                    overflow: "hidden",
                    border: "2px solid #E2A06A",
                    bgcolor: colors.primary[900],
                    mx: { xs: "auto", md: 0 },
                    "&:hover .imageOverlay": {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={formatImageUrl(request?.event?.event_flyer_image)}
                    alt="Event poster"
                    sx={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      objectFit: "cover",
                    }}
                  />

                  <Box
                    className="imageOverlay"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: "rgba(0,0,0,0.38)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      opacity: 0,
                      transition: "opacity 0.25s ease",
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      sx={{
                        borderRadius: "999px",
                        textTransform: "none",
                        boxShadow: "none",
                      }}
                    >
                      Download
                    </Button>

                    <IconButton
                      onClick={() => setOpenImage(true)}
                      sx={{
                        width: 42,
                        height: 42,
                        bgcolor: colors.primary[900],
                        "&:hover": { bgcolor: "#f2f2f2" },
                      }}
                    >
                      <OpenInFullIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 275,
                    height: 360,
                    mx: { xs: "auto", md: 0 },
                    borderRadius: "16px",
                    border: "2px solid #E2A06A",
                    bgcolor: mutedCard,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: textSecondary }}>
                    No Image Uploaded
                  </Typography>
                </Box>
              )}
            </Box>

            {/* DETAILS */}
            <Box sx={{ pt: { xs: 0, md: 1 } }}>
              <Typography
                sx={{
                  fontSize: 21,
                  fontWeight: 500,
                  color: "#171717",
                  lineHeight: 1.25,
                  mb: 0.5,
                }}
              >
                {request?.event?.title || "Tales of a Start-up"}
              </Typography>

              <Typography
                sx={{
                  fontSize: 15,
                  color: "#3E4856",
                  mb: 3,
                }}
              >
                Talk show
              </Typography>

              <Stack spacing={2.2}>
                <DetailRow
                  icon={
                    <CalendarIcon sx={{ fontSize: 20, fontWeight: "light" }} />
                  }
                  label="DATE"
                  value={
                    request?.event_datetime
                      ? new Date(request.event_datetime).toLocaleDateString(
                          "en-GB",
                          {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          },
                        )
                      : "-"
                  }
                />

                <DetailRow
                  icon={<LocationIcon sx={{ fontSize: 20 }} />}
                  label="VENUE"
                  value={request?.venue_name || "Not specified"}
                />

                <DetailRow
                  icon={<AccessTimeOutlinedIcon sx={{ fontSize: 20 }} />}
                  label="TIME"
                  value={
                    request?.event_datetime
                      ? new Date(request.event_datetime).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )
                      : "-"
                  }
                />
              </Stack>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* ACTION MODAL */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          {selectedAction && (
            <>
              <Typography sx={{ fontSize: 28, fontWeight: 700, mb: 1 }}>
                {modalContent[selectedAction].title}
              </Typography>

              <Typography sx={{ color: textSecondary, mb: 3 }}>
                {modalContent[selectedAction].subtitle}
              </Typography>
            </>
          )}

          {selectedAction === "DECLINED" && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  mb: 0.75,
                  fontSize: "12px",
                  lineHeight: "16px",
                  fontWeight: 500,
                  color: "#667085",
                }}
              >
                Reason
              </Typography>

              <Select
                fullWidth
                value={reason}
                onChange={(e) => {
                  const selected = declineReasons.find(
                    (x) => x.label === e.target.value,
                  );
                  setReason(e.target.value);
                  if (selected) setMessage(selected.message);
                }}
                displayEmpty
                sx={{
                  height: 38,
                  borderRadius: "6px",
                  backgroundColor: colors.primary[900],
                  fontSize: "12px",
                  color: "#344054",
                  boxShadow: "none",
                  "& .MuiSelect-select": {
                    py: "10px",
                    px: "12px",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#C9D3E0",
                    borderWidth: "1px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#B8C5D6",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#B8C5D6",
                    borderWidth: "1px",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 18,
                    color: "#667085",
                    right: 10,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 0.5,
                      borderRadius: "8px",
                      boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.08)",
                      border: "1px solid #EAECF0",
                      "& .MuiMenuItem-root": {
                        fontSize: "12px",
                        color: "#344054",
                        minHeight: 36,
                      },
                    },
                  },
                }}
              >
                {declineReasons.map((r) => (
                  <MenuItem key={r.label} value={r.label}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          <Box>
            <Typography
              sx={{
                mb: 0.75,
                fontSize: "12px",
                lineHeight: "16px",
                fontWeight: 500,
                color: "#667085",
              }}
            >
              Message
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                selectedAction === "DECLINED"
                  ? "Select a template above or create a custom message"
                  : "Write the WhatsApp message"
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  alignItems: "flex-start",
                  borderRadius: "6px",
                  backgroundColor: colors.primary[900],
                  fontSize: "12px",
                  color: "#344054",
                  "& fieldset": {
                    borderColor: "#C9D3E0",
                    borderWidth: "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#B8C5D6",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#B8C5D6",
                    borderWidth: "1px",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "12px",
                },
                "& .MuiOutlinedInput-inputMultiline": {
                  padding: "12px",
                  minHeight: "96px",
                  lineHeight: "18px",
                },
                "& textarea::placeholder": {
                  color: "#98A2B3",
                  opacity: 1,
                  fontSize: "12px",
                },
              }}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
            <Button onClick={() => setOpenModal(false)}>Cancel</Button>
            {/* <Button variant="contained" onClick={handleConfirm}>
              {selectedAction === "DECLINED"
                ? "Confirm Decline"
                : selectedAction === "ACCEPTED"
                  ? "Confirm Approval"
                  : "Open WhatsApp"}
            </Button> */}
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={saving}
            >
              {saving
                ? "Processing..."
                : selectedAction === "DECLINED"
                  ? "Confirm Decline"
                  : selectedAction === "ACCEPTED"
                    ? "Confirm Approval"
                    : "Confirm Maybe"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* IMAGE PREVIEW */}
      <Dialog
        open={openImage}
        onClose={() => setOpenImage(false)}
        maxWidth="lg"
      >
        <Box
          component="img"
          src={formatImageUrl(request?.event?.event_flyer_image)}
          alt="Full poster"
          sx={{
            display: "block",
            width: "100%",
            height: "auto",
            maxHeight: "85vh",
            bgcolor: colors.primary[900],
          }}
        />
      </Dialog>
    </Box>
  );
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          minWidth: 36,
          borderRadius: "12px",
          bgcolor: colors.primary[800],
          color: "#5F6B7A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: 12,
            color: "#98A2B3",
            lineHeight: 1.1,
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            mt: 0.35,
            fontSize: 16,
            fontWeight: 500,
            color: "#1D2939",
            lineHeight: 1.25,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}
